/**
 * Suggestions in the template editor — a provider that polls
 * `agentEditorSuggestions` while a template's own quote editor
 * (`/templates/documents/:id/latest/edit`) is open and visible, and the
 * pieces that show them: a severity marker after an item's title in the
 * tree (`ItemSuggestionMarker`) and the item panel's suggestion list
 * (`ItemSuggestionsBlock`). See design D6/D7:
 * - active only when the app-wide shared access probe
 *   (`useAgentAccessContext()`, `AgentAccessProvider`) is enabled and the
 *   path is a template's `latest/edit` — never a second `agentAccess` query
 *   that could disagree with the panel's;
 * - "hand to the assistant" goes through the domain-neutral
 *   `useAgentPanelControl().ask` seam;
 * - the client never sees fix ops, only `hasFix`/`fixLabel` and the item
 *   ids a fix would remove (`removesItemIds`), used to leave a deleted
 *   item's route;
 * - transport is GraphQL (design D7): one query
 *   (`AgentEditorSuggestions`) and two mutations (`ApplyAgentEditorSuggestion`,
 *   `DismissAgentEditorSuggestion`), `AgentSuggestions.graphql` colocated
 *   with this file. Called imperatively via `apolloClient.query`/`.mutate`
 *   (not the generated hooks) because this provider's own polling loop
 *   (variable 8 s/3 s interval, driven by `pending` and a manual `refresh()`
 *   tick) needs full control over when each request fires — the same
 *   reasoning `useAgentAccess.ts` documents for its own probe.
 */
import { ApolloError, useApolloClient } from "@apollo/client";
import {
  ErrorOutlined as ErrorIcon,
  LightbulbOutlined as IdeaIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AgentEditorSuggestionsDocument,
  ApplyAgentEditorSuggestionDocument,
  DismissAgentEditorSuggestionDocument,
  type AgentEditorSuggestionsQuery,
  type AgentEditorSuggestionsQueryVariables,
  type ApplyAgentEditorSuggestionMutation,
  type ApplyAgentEditorSuggestionMutationVariables,
  type DismissAgentEditorSuggestionMutation,
  type DismissAgentEditorSuggestionMutationVariables,
} from "./AgentSuggestions.generated.js";
import { useAgentPanelControl } from "./agentPanelControl.js";
import { derivePageContext } from "./pageContext.js";
import { useAgentAccessContext } from "./AgentAccessProvider.js";
import { useAgentIdentityKey } from "./useAgentAccess.js";

export type EditorSuggestion = {
  id: string;
  itemId: string;
  severity: "error" | "warning" | "idea";
  source: "rule" | "model" | "product";
  title: string;
  detail: string;
  // Optional, not just nullable: codegen types a nullable GraphQL field as
  // optional (`fixLabel?: string | null`) — matching that shape here lets
  // the generated query's `suggestions` nodes assign directly, with no
  // per-field mapping step.
  fixLabel?: string | null;
  hasFix: boolean;
  /** Item ids a fix would delete, used to leave that item's route. */
  removesItemIds: string[];
  assistantPrompt?: string | null;
};

type SuggestionsState = {
  templateId: string | null;
  byItem: Map<string, EditorSuggestion[]>;
  apply: (suggestion: EditorSuggestion) => Promise<void>;
  dismiss: (suggestion: EditorSuggestion, reason: string) => Promise<void>;
  /** Re-review now, e.g. after applying a fix. */
  refresh: () => void;
  busyId: string | null;
  error: string | null;
  /** The model is still reviewing the latest change. */
  pending: boolean;
};

const SuggestionsContext = React.createContext<SuggestionsState>({
  templateId: null,
  byItem: new Map(),
  apply: async () => undefined,
  dismiss: async () => undefined,
  refresh: () => undefined,
  busyId: null,
  error: null,
  pending: false,
});

export const useAgentSuggestions = () => React.useContext(SuggestionsContext);

/**
 * After a suggestion's fix changed a template on the server: if it deleted
 * anything while an item is open in the editor, go back to the template
 * first — refetching the open item's panel would query an item that no
 * longer exists and fail. Then let every active query (tree, panel,
 * preview) show the change.
 */
export function useRefreshAfterAgentWrite() {
  const apollo = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  return React.useCallback(
    async (deletedItemIds: string[]) => {
      const open = /^(.*\/latest\/edit)\/items\/([0-9a-f-]{36})/.exec(
        location.pathname
      );
      // Deleting a section removes its children too, and the open item may
      // be one of them; the deleted ids alone do not tell, so leave on any
      // delete.
      const gone = new Set(deletedItemIds);
      if (open && open[1] && open[2] && deletedItemIds.length > 0) {
        gone.add(open[2]);
        navigate(open[1], { replace: true });
        // The router may render the new route in a transition: wait until
        // the item panel is really gone, so its query is no longer active.
        const deadline = Date.now() + 2000;
        while (
          window.location.pathname.includes("/items/") &&
          Date.now() < deadline
        )
          await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
      }
      await apollo.refetchQueries({
        include: "active",
        // Never ask the server for an item that no longer exists ("Tree
        // node not found"): skip a query that is about one item — a
        // variable that IS a deleted (or the open) item id. A list such as
        // the editor's expandedItemIds may still name it; that query must
        // be refetched, or the tree keeps showing the deleted section.
        onQueryUpdated: query =>
          !Object.values(query.variables ?? {}).some(
            value => typeof value === "string" && gone.has(value)
          ),
      });
    },
    [apollo, navigate, location.pathname]
  );
}

const POLL_MS = 8000;
/** While the model reviews, look again sooner so its suggestions appear promptly. */
const POLL_PENDING_MS = 3000;

/**
 * Every `Agent_*Error` (`agentGraphqlErrors.ts`'s `AGENT_ERROR_CODES`)
 * reaches the client with a stable `extensions.code`: branch on the code,
 * never on message text, which can be reworded without notice.
 */
const AGENT_NOT_FOUND_CODE = "AGENT_NOT_FOUND";
const AGENT_ACCESS_DENIED_CODE = "AGENT_ACCESS_DENIED";

/** The first GraphQL error's `extensions.code`, if `err` is an `ApolloError` carrying one — `null` otherwise. */
function agentGraphqlErrorCode(err: unknown): string | null {
  if (!(err instanceof ApolloError)) return null;
  const code = err.graphQLErrors[0]?.extensions?.["code"];
  return typeof code === "string" ? code : null;
}

function isAgentSuggestionNotFound(code: string | null): boolean {
  return code === AGENT_NOT_FOUND_CODE;
}

function isAgentAccessDenied(code: string | null): boolean {
  return code === AGENT_ACCESS_DENIED_CODE;
}

export function AgentSuggestionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslate("Assistant");
  const location = useLocation();
  const access = useAgentAccessContext();
  const identityKey = useAgentIdentityKey();
  const apollo = useApolloClient();
  const refreshAfterWrite = useRefreshAfterAgentWrite();
  const page = derivePageContext(location.pathname);
  const templateId =
    access.enabled &&
    page.templateId &&
    location.pathname.includes("/latest/edit")
      ? page.templateId
      : null;

  const [suggestions, setSuggestions] = React.useState<EditorSuggestion[]>([]);
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [tick, setTick] = React.useState(0);
  const [pending, setPending] = React.useState(false);
  const disabled = React.useRef(false);
  // The template and user the provider currently shows, as one key. An
  // in-flight `apply` captures it when it starts and drops its continuation
  // once it no longer matches — see the reset effect below and `apply`.
  const scopeKey = `${templateId ?? ""}|${identityKey}`;
  const currentScope = React.useRef(scopeKey);

  // Reset when the template (or access) changes, or the selected
  // user/organisation switches (`identityKey`, same seam `AgentPanel.tsx`
  // uses) — never on a refresh tick, or an in-flight re-review would flash
  // every marker away and back. Suggestions are plain local state from an
  // imperative poll, not a reactively re-fetched query, so without this the
  // previous user's suggestion titles/details would stay visible on screen
  // until the next poll resolves (indefinitely on a failed poll) — a real
  // privacy boundary, since the backend scopes suggestions strictly per
  // user. A shown apply error is cleared too: every selected item's panel
  // renders it, so it would otherwise outlive the template/user it belongs
  // to until the next apply overwrites it. This clears state once, at the
  // switch: an apply still in flight then must not write into the new
  // template or user either, so `currentScope` is moved on here too and
  // `apply` skips its continuation (suggestion removal, the post-write
  // refresh and its navigation, the re-review tick, its error) when the
  // scope it started in is gone. The session stop (`disabled`) is reset
  // too: a denied review holds for that template and user only, not for
  // every other one until a reload. This effect must stay declared before
  // the polling effect, so the reset is in place when that effect runs for
  // the same change.
  React.useEffect(() => {
    currentScope.current = scopeKey;
    disabled.current = false;
    setSuggestions([]);
    setPending(false);
    setError(null);
    // `scopeKey` is derived from exactly `templateId` and `identityKey`.
  }, [templateId, identityKey, scopeKey]);

  React.useEffect(() => {
    if (!templateId || disabled.current) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const review = async () => {
      let next = POLL_MS;
      if (document.visibilityState === "visible" && !disabled.current) {
        try {
          const { data } = await apollo.query<
            AgentEditorSuggestionsQuery,
            AgentEditorSuggestionsQueryVariables
          >({
            query: AgentEditorSuggestionsDocument,
            variables: { templateId },
            fetchPolicy: "network-only",
            // Every error here is already handled by hand (the session-stop
            // above), so the global Apollo `onError` link must not also
            // toast the raw backend text on top.
            context: { noErrorNotification: true },
          });
          // A refused `template.document.read`/`aiAssistant` resolves to the
          // auto-wrapped `MissingPermissions`/`MissingCapabilities` union
          // member here (not a thrown error) — treated as "no suggestions
          // this poll", same as an empty result, without disabling polling
          // for the session (only a thrown `Agent_AccessDeniedError`, below,
          // does that).
          const result = getDataOrNull(data.agentEditorSuggestions);
          if (!cancelled) {
            setSuggestions(result?.suggestions ?? []);
            setPending(result?.pending === true);
          }
          if (result?.pending) next = POLL_PENDING_MS;
        } catch (err) {
          // No more access (the org's `aiAssistant` capability was toggled
          // off): stay silent for this template and user until either
          // changes (the reset effect above) — keyed off the specific
          // thrown `Agent_AccessDeniedError`'s `extensions.code`, not an
          // HTTP status (there is none any more; see `agentGraphqlErrorCode`'s
          // doc comment). A late denial for a template or user that is no
          // longer shown (`cancelled`) must not stop the new one's polling.
          if (isAgentAccessDenied(agentGraphqlErrorCode(err)) && !cancelled) {
            disabled.current = true;
            // Access can be revoked mid-session — clear anything already
            // rendered, not only future polls.
            setSuggestions([]);
            setPending(false);
          }
        }
      }
      if (!cancelled && !disabled.current)
        timer = setTimeout(() => void review(), next);
    };
    void review();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // `identityKey` is included so React tears down (via the `cancelled`
    // flag above) and restarts this effect on a selected-user switch,
    // discarding any in-flight response for the previous identity — the
    // reset effect above only clears the state that a stale response would
    // otherwise repopulate with the wrong user's private data.
  }, [templateId, apollo, tick, identityKey]);

  const byItem = React.useMemo(() => {
    const map = new Map<string, EditorSuggestion[]>();
    for (const s of suggestions)
      map.set(s.itemId, [...(map.get(s.itemId) ?? []), s]);
    return map;
  }, [suggestions]);

  const refresh = React.useCallback(() => setTick(t => t + 1), []);

  const apply = React.useCallback(
    async (suggestion: EditorSuggestion) => {
      if (!templateId) return;
      // A switch while the mutation is in flight (the reset effect moved
      // `currentScope` on) makes everything below stale: `refreshAfterWrite`
      // still holds the old template's pathname and would navigate back to
      // its editor, and a late error would show on the new one.
      const startedIn = scopeKey;
      const stillInScope = () => currentScope.current === startedIn;
      setBusyId(suggestion.id);
      setError(null);
      try {
        await apollo.mutate<
          ApplyAgentEditorSuggestionMutation,
          ApplyAgentEditorSuggestionMutationVariables
        >({
          mutation: ApplyAgentEditorSuggestionDocument,
          variables: { input: { templateId, suggestionId: suggestion.id } },
          // The catch below already renders a translated message — the
          // global Apollo error link must not toast the raw one too.
          context: { noErrorNotification: true },
        });
        if (!stillInScope()) return;
        setSuggestions(list => list.filter(s => s.id !== suggestion.id));
        // The change happened on the server; let the editor show it. This is
        // best-effort: the apply is committed, so a failed refetch or
        // navigation must not report it as failed (a retry would only hit
        // `Agent_NotFoundError`).
        try {
          await refreshAfterWrite(suggestion.removesItemIds);
        } catch {
          // Swallowed on purpose — the next poll re-syncs the editor.
        }
        refresh();
      } catch (err) {
        if (!stillInScope()) return;
        const code = agentGraphqlErrorCode(err);
        if (isAgentSuggestionNotFound(code))
          setError(t("This suggestion is no longer available"));
        else if (isAgentAccessDenied(code))
          setError(t("You are not allowed to change this template"));
        else setError(t("The suggestion could not be applied"));
      } finally {
        setBusyId(null);
      }
    },
    [apollo, refreshAfterWrite, refresh, templateId, scopeKey, t]
  );

  const dismiss = React.useCallback(
    async (suggestion: EditorSuggestion, reason: string) => {
      if (!templateId) return;
      const startedIn = scopeKey;
      setSuggestions(list => list.filter(s => s.id !== suggestion.id));
      setError(null);
      try {
        await apollo.mutate<
          DismissAgentEditorSuggestionMutation,
          DismissAgentEditorSuggestionMutationVariables
        >({
          mutation: DismissAgentEditorSuggestionDocument,
          variables: {
            input: { templateId, suggestionId: suggestion.id, reason },
          },
          // The catch below already renders a translated message — the
          // global Apollo error link must not toast the raw one too.
          context: { noErrorNotification: true },
        });
      } catch (err) {
        // A suggestion that is no longer available is gone either way.
        // Any other failure left it open: say so and review again, so it
        // comes back right away instead of silently on the next poll.
        if (
          currentScope.current !== startedIn ||
          isAgentSuggestionNotFound(agentGraphqlErrorCode(err))
        )
          return;
        setError(t("The suggestion could not be dismissed"));
        refresh();
      }
    },
    [apollo, templateId, scopeKey, refresh, t]
  );

  const value = React.useMemo(
    () => ({
      templateId,
      byItem,
      apply,
      dismiss,
      refresh,
      busyId,
      error,
      pending,
    }),
    [templateId, byItem, apply, dismiss, refresh, busyId, error, pending]
  );
  return (
    <SuggestionsContext.Provider value={value}>
      {children}
    </SuggestionsContext.Provider>
  );
}

const SEVERITY_ORDER = { error: 0, warning: 1, idea: 2 } as const;
/** Matches the backend's dismiss reason limit — a longer one 400s silently. */
const DISMISS_REASON_MAX = 500;

function SeverityIcon({
  severity,
  size,
}: {
  severity: EditorSuggestion["severity"];
  size: number;
}) {
  const sx = { fontSize: size };
  if (severity === "error") return <ErrorIcon color="error" sx={sx} />;
  if (severity === "warning") return <WarningIcon color="warning" sx={sx} />;
  return <IdeaIcon color="info" sx={sx} />;
}

/** Small severity marker after an item's title in the editor tree. */
export function ItemSuggestionMarker({ itemId }: { itemId: string }) {
  const { t } = useTranslate("Assistant");
  const { byItem } = useAgentSuggestions();
  const list = byItem.get(itemId);
  if (!list || list.length === 0) return null;
  const worst = [...list].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  )[0];
  if (!worst) return null;
  return (
    <Tooltip
      title={
        <Box>
          {list.map(s => (
            <Typography key={s.id} variant="caption" component="div">
              • {s.title}
            </Typography>
          ))}
          <Typography
            variant="caption"
            component="div"
            sx={{ mt: 0.5, opacity: 0.8 }}
          >
            {t("Open the item for details")}
          </Typography>
        </Box>
      }
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          ml: 0.5,
          verticalAlign: "middle",
        }}
      >
        <SeverityIcon severity={worst.severity} size={14} />
        {list.length > 1 && (
          <Typography
            component="span"
            variant="caption"
            sx={{ ml: 0.25, fontSize: 10 }}
          >
            {list.length}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

export function SuggestionCard({
  suggestion,
}: {
  suggestion: EditorSuggestion;
}) {
  const { t } = useTranslate(["Assistant", "Global"]);
  const { apply, dismiss, busyId } = useAgentSuggestions();
  const { ask } = useAgentPanelControl();
  const [asking, setAsking] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const dismissLabel = suggestion.hasFix
    ? t("Discard", { ns: "Global" })
    : t("Hide");
  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
        <Box sx={{ pt: 0.25 }}>
          <SeverityIcon severity={suggestion.severity} size={18} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography component="div" variant="body2" sx={{ fontWeight: 600 }}>
            {suggestion.title}
            {suggestion.source !== "rule" && (
              <Chip
                size="small"
                label={t("AI")}
                sx={{ ml: 1, height: 16, fontSize: 10 }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {suggestion.detail}
          </Typography>
          {asking ? (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={t(
                  "Why not? (optional, helps the assistant learn)"
                )}
                value={reason}
                onChange={event => setReason(event.target.value)}
                slotProps={{ htmlInput: { maxLength: DISMISS_REASON_MAX } }}
              />
              <Button
                size="small"
                onClick={() => void dismiss(suggestion, reason.trim())}
              >
                {dismissLabel}
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {suggestion.hasFix && (
                <Button
                  size="small"
                  variant="contained"
                  disabled={busyId !== null}
                  onClick={() => void apply(suggestion)}
                >
                  {suggestion.fixLabel ?? t("Apply")}
                </Button>
              )}
              {!suggestion.hasFix && suggestion.assistantPrompt && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    if (suggestion.assistantPrompt)
                      ask(suggestion.assistantPrompt);
                  }}
                >
                  {t("Let the assistant do it")}
                </Button>
              )}
              <Button size="small" onClick={() => setAsking(true)}>
                {dismissLabel}
              </Button>
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

/** The selected item's suggestions, at the top of the item panel. */
export function ItemSuggestionsBlock({ itemId }: { itemId: string }) {
  const { t } = useTranslate("Assistant");
  const { byItem, error, pending } = useAgentSuggestions();
  const list = [...(byItem.get(itemId) ?? [])].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );
  if (list.length === 0 && !error && !pending) return null;
  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {pending && (
        <Typography variant="caption" color="text.secondary">
          {t("The assistant is reviewing the last change…")}
        </Typography>
      )}
      {list.map(s => (
        <SuggestionCard key={s.id} suggestion={s} />
      ))}
    </Stack>
  );
}
